import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Typography, Box, Card, CardContent, CardActionArea, Grid,
  Breadcrumbs, Link, Skeleton, Chip, alpha, useTheme,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { listArticles } from '../../utils/api';
import { Article } from '../../utils/types';

const CategoryView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const theme = useTheme();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await listArticles(slug);
        setArticles(data.filter(a => a.status === 'published'));
      } catch (err) {
        console.error('Failed to load articles', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={30} />
        <Skeleton variant="text" width={300} height={50} sx={{ mt: 2 }} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={160} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">Help Center</Link>
        <Typography color="text.primary">{slug}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" fontWeight={700} gutterBottom sx={{ textTransform: 'capitalize' }}>
        {slug?.replace(/-/g, ' ')}
      </Typography>

      {articles.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No articles in this category yet.
        </Typography>
      ) : (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {articles.map((article) => (
            <Grid item xs={12} sm={6} md={4} key={article.id}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea component={RouterLink} to={`/article/${article.slug}`} sx={{ height: '100%' }}>
                  <CardContent>
                    {article.videoUrl && (
                      <Chip
                        label="Video"
                        size="small"
                        color="secondary"
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {article.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {article.excerpt}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      {new Date(article.updatedAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CategoryView;
